import { FC, useEffect, useState } from 'react';
import { z } from "zod";

import {
  Select, FormControlLabel, Radio, RadioGroup, MenuItem,
  FormControl, InputLabel, Grid, Typography, DialogActions,
  DialogContent, FormHelperText, TextField, Divider,
  CircularProgress, styled
} from '@mui/material';
import Banner from "@/components/Banner/Banner";
import { useTranslation } from 'react-i18next';
import { useContracts } from '@/components/Contracts/hooks/useContracts';
import { ContractDisplayTypes } from '@/components/Contracts/types/contracts';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { ChangeAddressData } from '@/components/Contracts/types/address';
import { useAddress } from '@/components/Contracts/hooks/useAddress';
import AddressForm from '@/components/form-fields/AddressForm/AddressForm';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@gisce/oficina-virtual-components';
import { LoadingAnimation } from '@/components/LoadingAnimation';
import { MuiTelInput } from 'mui-tel-input'
import { default as contractActions } from "@/components/Contracts/actions/newActions"
import { useAppDispatch } from '@/hooks';
import { zodAddressSchema } from '@/components/form-fields/AddressForm/zod/zodSchema';
import { useSettingsContext } from '@/components/Settings/hooks/useSettingsContext';

interface ChangeAddressProps {
  type: ContractDisplayTypes;
  onClose: () => void;
}

const NoFlagMuiTelInputStyled = styled(MuiTelInput)`
  .MuiTelInput-IconButton {
    display: none;
  }
`;

const ChangeAddress: FC<ChangeAddressProps> = ({
  type,
  onClose
}) => {
  const dispatch = useAppDispatch();
  const Settings = useSettingsContext();

  const { t } = useTranslation();
  const { currentContract } = useContracts(type);
  const { availableAddresses, state, createAddress, updateAddress } = useAddress(type);

  const readCurrentInfo = Settings.contract?.addressChange?.readCurrentInfo;
  const enableExisting = Settings.contract?.addressChange?.enableExisting;
  const readOnlyAddressInfo = Settings.contract?.addressChange?.readOnlyAddressInfo;
  const notificationAddress = currentContract?.notification_address;

  const [bannerLoaded, setBannerLoaded] = useState(false);

  const schema = z.discriminatedUnion("alreadyRegistered", [
    z.object({
      alreadyRegistered: z.literal('existing'),
      addressId: z.number()
    }),
    z.object({
      alreadyRegistered: z.literal('new'),
      address: zodAddressSchema.extend({
        name: z.string()
      })
    })
  ]) satisfies z.ZodType<ChangeAddressData>;

  const methods = useForm<ChangeAddressData>({
    resolver: zodResolver(schema),
    defaultValues: {
      alreadyRegistered: "new",
      address: {
        name: notificationAddress?.name,
        state: readCurrentInfo && notificationAddress?.state_id?.id || '',
        ine: '',
        poblacio: readCurrentInfo && notificationAddress?.city || '',
        tv: readCurrentInfo && Number(notificationAddress?.tv?.id) || '', // Street Type (Tipo Via)
        nv: readCurrentInfo && notificationAddress?.nv || '', // Street Name (Nombre Via)
        pnp: readCurrentInfo && notificationAddress?.pnp || '', // Street Number
        bq: readCurrentInfo && String(notificationAddress?.bq) || '', // Block (Bloque)
        es: readCurrentInfo && notificationAddress?.es || '', // Stair (Escalera)
        pt: readCurrentInfo && notificationAddress?.pt || '', // Floor
        pu: readCurrentInfo && notificationAddress?.pu || '', // Door (Puerta)
        dp: readCurrentInfo && notificationAddress?.zip || '', // Dirección Postal (Zip)
        aclarador: readCurrentInfo && notificationAddress?.aclarador || '',
        email: readCurrentInfo && notificationAddress?.email || '',
        mobile: readCurrentInfo && notificationAddress?.mobile || '',
      }
    }
  });

  const {
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = methods;

  const watchedAlreadyRegistered = watch("alreadyRegistered");

  useEffect(() => {
    if (state === "success") {
      dispatch(contractActions[type].notificationFormClosed());
      handleClose();
    }
  }, [state]);

  const onChangeAddressSubmit = (formValues: ChangeAddressData) => {
    if (formValues.alreadyRegistered === 'existing') {
      if (currentContract) {
        updateAddress(formValues.addressId, currentContract.name);
      }
    }
    else {
      if (currentContract) {
        createAddress(formValues.address, currentContract.name);
      }
    }
  }

  const handleClose = () => {
    onClose();
  };

  const addressChoices = availableAddresses.map((address) => {
    const label = <div style={{ marginTop: '2vh' }}>
      {`${address.name}`}
      <br />
      {`${address.full_address}`}
      <br />
      {`${address.email}`}
      <br />
      {`${address.mobile}`}
    </div>;
    return <FormControlLabel key={address.id} value={address.id} control={<Radio />} label={label} />;
  });

  const renderInitForm = () => {
    return <>
      <FormProvider {...methods}>
        <form onSubmit={
          handleSubmit(onChangeAddressSubmit)
        }>

          <DialogContent>

            {enableExisting &&
              <Controller
                name="alreadyRegistered"
                control={control}
                render={({ field }) => (
                  <FormControl style={{ width: "100%", marginBottom: watchedAlreadyRegistered === 'new' ? 20 : 0 }}>
                    <InputLabel shrink>
                      {t('common:text.change_address_type')}
                    </InputLabel>
                    <Select
                      {...field}
                    >
                      <MenuItem key="existing" value="existing">{t('common:text.change_address_existing')}</MenuItem>
                      <MenuItem key="new" value="new">{t('common:text.change_address_new')}</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            }

            <>
              {watchedAlreadyRegistered === 'new' && <>
                <Grid container spacing={3} style={{ marginBottom: 20 }}>
                  {
                    <Grid item xs={12}>
                      <Controller
                        name="address.name"
                        control={control}
                        render={({ field, fieldState }) => (<>
                          <TextField
                            {...field}
                            label={t('common:text.address_name')}
                            style={{ width: "100%" }}
                            error={!!(fieldState.error)}
                          />
                          {fieldState.error?.message && (
                            <FormHelperText error={true}>
                              {t(fieldState.error.message)}
                            </FormHelperText>
                          )}
                        </>)}
                      />
                    </Grid>
                  }
                  {
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="address.mobile"
                        control={control}
                        render={({ field, fieldState }) => (<>
                          <NoFlagMuiTelInputStyled
                            {...field}
                            label={t('common:text.address_phone')}
                            style={{ width: "100%" }}
                            error={!!(fieldState.error)}
                            disableDropdown={true}
                            inputProps={{ maxLength: 16 }}
                            // forceCallingCode={true}
                            defaultCountry="ES"
                            fullWidth={true}
                          />
                          {fieldState.error?.message && (
                            <FormHelperText error={true}>
                              {t(fieldState.error.message)}
                            </FormHelperText>
                          )}
                        </>)}
                      />
                    </Grid>
                  }
                  {
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="address.email"
                        control={control}
                        render={({ field, fieldState }) => (<>
                          <TextField
                            {...field}
                            label={t('common:text.address_email')}
                            style={{ width: "100%" }}
                            error={!!(fieldState.error)}
                          />
                          {fieldState.error?.message && (
                            <FormHelperText error={true}>
                              {t(fieldState.error.message)}
                            </FormHelperText>
                          )}
                        </>)}
                      />
                    </Grid>
                  }
                </Grid>

                <AddressForm
                  titleType='subtitle1'
                  readOnly={readOnlyAddressInfo}
                  showTitle={readOnlyAddressInfo}
                  showRefCatastral={false}
                />
              </>}

              {watchedAlreadyRegistered === 'existing' &&
                <div>
                  {!(state === "fetching") ?
                    availableAddresses.length ?
                      <>
                        <Controller
                          name="addressId"
                          control={control}
                          render={({ field }) => (
                            <RadioGroup
                              {...field}
                              value={field.value ?? currentContract?.notification_address.id ?? null}
                              onChange={event => field.onChange(Number(event.target.value))}
                            >
                              {addressChoices}
                            </RadioGroup>
                          )}
                        />
                      </>
                      :
                      <p style={{ padding: '1vh' }}>
                        {t('common:text.change_address_no_data')}
                      </p>
                    :
                    <LoadingAnimation />
                  }
                </div>
              }
            </>

          </DialogContent>

          <DialogActions sx={{ display: "flex", justifyContent: "flex-start", paddingLeft: 24 }}>
            <Button
              variant={'outlined'}
              color={"primary"}
              onClick={handleClose}
            >
              {t('common:text.power_modification_close')}
            </Button>
            <Button
              type="submit"
              variant={'contained'}
              color={'primary'}
              disabled={isSubmitting || state === "submitting"}
            >
              {isSubmitting || state === "submitting" ? <CircularProgress size={25} /> : null}
              {t('common:text.iban_change_update')}
            </Button>
          </DialogActions>

        </form>
      </FormProvider>
    </>
  };

  return (
    <Grid
      container
      direction="row"
      alignItems="stretch"
    >
      <Grid item xs={12} style={{ padding: 12 }}>
        <Typography variant="h5" style={{ paddingLeft: 24 }}>{t('common:text.change_address_edit')}</Typography>
        {renderInitForm()}
      </Grid>
      <Grid item xs={6} style={{ padding: bannerLoaded ? 12 : 0 }}>
        <Banner code='canvi_change_address_ov' onContentChange={setBannerLoaded} />
      </Grid>
    </Grid>
  );
};

export default ChangeAddress;
